import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faPlus, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { crmService } from '../services/crmService';
import './OfferGenerator.css';

/**
 * Offer Generator CRM Partner Search Component
 * 
 * Features:
 * - Debounced search (300ms)
 * - Filters APPROVED profiles only
 * - Displays Legal Entity Name and Trading Short Name
 * - Click outside to close
 * - Matches OfferGenerator form styling
 * 
 * @param {Object} props
 * @param {string} props.value - Current partner name value
 * @param {Function} props.onChange - Callback with (partnerName)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 */
const OfferPartnerSearch = ({ 
  value = '', 
  onChange,
  placeholder = 'Search CRM profiles...',
  className = '',
  onCreatePartner,
  extraVisibleProfiles = [],
}) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [isPartnerSelected, setIsPartnerSelected] = useState(!!value && value !== 'Data from CRM');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [selectedAddress, setSelectedAddress] = useState('');
  const dropdownRef = useRef(null);

  // Update search term when value changes externally
  useEffect(() => {
    if (value && value !== 'Data from CRM' && value !== searchTerm) {
      setSearchTerm(value);
      setIsPartnerSelected(true);
    } else if (!value || value === 'Data from CRM') {
      setIsPartnerSelected(false);
      if (value === 'Data from CRM') {
        setSearchTerm('');
      }
    }
  }, [value]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch partners when debounced search changes
  useEffect(() => {
    if (debouncedSearch.trim().length >= 2 && !isPartnerSelected) {
      fetchApprovedPartners(debouncedSearch);
    } else {
      setFilteredPartners([]);
      setSearchFailed(false);
    }
  }, [debouncedSearch, isPartnerSelected]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchApprovedPartners = async (search) => {
    try {
      setLoading(true);
      setSearchFailed(false);
      
      // Use the IsAuthenticated-only quick-search endpoint so trading
      // users without HasCRMAccess can still see existing partners and
      // avoid creating duplicate stubs via the inline create CTA.
      const results = await crmService.quickSearchProfiles(search);
      
      // Filter for APPROVED profiles only
      const approvedProfiles = results.filter(profile => 
        (profile.approval_status || '').toString().toLowerCase() === 'approved' || profile.approval_status === undefined
      );
      
      const partnersData = approvedProfiles.map(profile => ({
        id: profile.id,
        legal_entity_name: profile.legal_entity_name,
        trading_short_name: profile.trading_short_name,
        head_office_address: profile.head_office_address || '',
        approval_status: profile.approval_status || 'approved',
        display_name: profile.trading_short_name 
          ? `${profile.legal_entity_name} (${profile.trading_short_name})`
          : profile.legal_entity_name
      }));
      
      setFilteredPartners(partnersData);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error fetching approved partners:', error);
      setFilteredPartners([]);
      setSearchFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Reset keyboard highlight whenever the visible results change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [filteredPartners, showDropdown]);

  // Merge approved hits with any session-pending (just-created)
  // profiles whose name matches the current search. Approved
  // results come first; pending stubs are shown at the bottom.
  const mergedPartners = (() => {
    const term = debouncedSearch.trim().toLowerCase();
    const approvedIds = new Set(filteredPartners.map(p => p.id));
    const matchingExtras = (extraVisibleProfiles || []).filter(p => {
      if (!p || approvedIds.has(p.id)) return false;
      const hay = (
        (p.legal_entity_name || '') + ' ' + (p.trading_short_name || '')
      ).toLowerCase();
      return hay.includes(term);
    }).map(p => ({
      id: p.id,
      legal_entity_name: p.legal_entity_name,
      trading_short_name: p.trading_short_name,
      head_office_address: p.head_office_address || '',
      approval_status: p.approval_status || 'pending',
      display_name: p.trading_short_name
        ? `${p.legal_entity_name} (${p.trading_short_name})`
        : p.legal_entity_name,
    }));
    return [...filteredPartners, ...matchingExtras];
  })();

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      return;
    }
    if (!showDropdown || searchFailed || mergedPartners.length === 0) return;
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, mergedPartners.length - 1));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelectPartner(mergedPartners[highlightIndex]);
    }
  };

  const handleSelectPartner = (partner) => {
    setSearchTerm(partner.legal_entity_name);
    setShowDropdown(false);
    setIsPartnerSelected(true);
    setSelectedAddress(partner.head_office_address || '');

    // Pass partner name to parent
    if (onChange) {
      onChange(partner.legal_entity_name);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setIsPartnerSelected(false);
    setSelectedAddress('');
    setFilteredPartners([]);
    if (onChange) {
      onChange('');
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <div className="offer-partner-search-wrapper" style={{ position: 'relative' }}>
        {!isPartnerSelected && (
          <FontAwesomeIcon 
            icon={faSearch} 
            className="offer-partner-search-icon"
          />
        )}
        <input
          type="text"
          className={`offer-partner-search-input ${className}`}
          value={searchTerm}
          onChange={(e) => {
            const newValue = e.target.value;
            setSearchTerm(newValue);
            setShowDropdown(true);
            setIsPartnerSelected(false);
            setSelectedAddress('');

            // If input is cleared, clear the stored field values
            if (newValue === '') {
              if (onChange) {
                onChange('');
              }
            }
          }}
          onFocus={() => {
            if (searchTerm.length >= 2 && !isPartnerSelected) {
              setShowDropdown(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          data-has-icon={!isPartnerSelected}
          data-selected={isPartnerSelected}
        />
        {searchTerm && (
          <FontAwesomeIcon 
            icon={faTimes} 
            onClick={handleClear}
            className="offer-partner-clear-icon"
          />
        )}
      </div>
      {isPartnerSelected && selectedAddress && (
        <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <FontAwesomeIcon icon={faMapMarkerAlt} /> {selectedAddress}
        </div>
      )}

      {showDropdown && debouncedSearch.trim().length >= 2 && (
        <div className="offer-partner-dropdown" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '2px solid #4a6b25',
          borderRadius: '8px',
          maxHeight: '280px',
          overflowY: 'auto',
          zIndex: 1000,
          marginTop: '4px',
          boxShadow: '0 4px 12px rgba(74, 107, 37, 0.2)'
        }}>
          {(() => {
            const merged = mergedPartners;

            if (loading) {
              return (
                <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                  Searching CRM...
                </div>
              );
            }
            if (searchFailed) {
              // Row 127: a failed search is not "no partners found" -- and
              // never offers to create one.
              return (
                <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                  Search failed —{' '}
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      fetchApprovedPartners(debouncedSearch);
                    }}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#4a6b25', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                  >
                    try again
                  </button>
                </div>
              );
            }
            return (
              <>
                {merged.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                    No APPROVED partners found for "{debouncedSearch}"
                  </div>
                ) : (
                  merged.map((partner, idx) => {
                    const isPending = (partner.approval_status || '').toLowerCase() === 'pending';
                    return (
                      <div
                        key={partner.id}
                        ref={idx === highlightIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
                        onClick={() => handleSelectPartner(partner)}
                        className="offer-partner-dropdown-item"
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          transition: 'background-color 0.2s',
                          backgroundColor: idx === highlightIndex ? '#f0f8ff' : 'white'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f8ff'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx === highlightIndex ? '#f0f8ff' : 'white'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: '600', color: '#4a6b25' }}>
                            {partner.legal_entity_name}
                          </span>
                          {isPending && (
                            <span
                              title="Created from this draft. Awaiting CRM approval."
                              style={{
                                fontSize: '0.7em',
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                border: '1px solid #f59e0b',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Pending Approval
                            </span>
                          )}
                        </div>
                        {partner.trading_short_name && (
                          <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                            Trading as: {partner.trading_short_name}
                          </div>
                        )}
                        {partner.head_office_address && (
                          <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} /> {partner.head_office_address}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {onCreatePartner && (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowDropdown(false);
                      onCreatePartner(debouncedSearch);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      border: 'none',
                      borderTop: '1px solid #e5e7eb',
                      backgroundColor: '#f0f8ff',
                      color: '#4a6b25',
                      fontWeight: 600,
                      fontSize: '0.9em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Create CRM partner "{debouncedSearch}"
                  </button>
                )}
              </>
            );
          })()}
        </div>
      )}
      {debouncedSearch.trim().length > 0 && debouncedSearch.trim().length < 2 && !isPartnerSelected && (
        <span style={{ fontSize: '0.85em', color: '#666', marginTop: '2px', display: 'block' }}>
          Type at least 2 characters to search CRM profiles
        </span>
      )}
    </div>
  );
};

export default OfferPartnerSearch;
